/**
 * app/screens/RoomList.tsx
 * -------------------------
 * What: Lists rooms and provides navigation to add/edit rooms and view items by room.
 * Why: Organises items spatially for easier browsing.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  Modal,
  Animated,
  useWindowDimensions,
  Platform,
  Pressable,
  SafeAreaView,
  StatusBar,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, CommonActions, useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import { Ionicons } from '@expo/vector-icons';
import ThemedButton from '../components/ThemedButton';
import { useSubscription } from '../../lib/SubscriptionContext';
import { usePaginatedRooms } from '../../lib/DataContext';
import { useTheme } from '../../lib/ThemeContext';
import ReactNative from 'react-native';
import FormTextInput from '../components/FormTextInput';

const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
  });
};

const toSentenceCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

type Room = {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  items: Array<{ count: number }>;
};

type RoomListScreenNavigationProp = StackNavigationProp<RootStackParamList>;
type RoomListScreenRouteProp = RouteProp<RootStackParamList, 'RoomsTab'>;
type SortOption = 'recent' | 'name';

// Debounce hook
function useDebouncedValue(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// Move isSmallScreen check outside component
const useIsSmallScreen = () => {
  const { width } = useWindowDimensions();
  return width < 768;
};

// Create a function to generate styles based on screen size
const createStyles = (isSmallScreen: boolean) => StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    gap: 12,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
    borderWidth: 1,
    ...(Platform.OS === 'web' && !isSmallScreen ? {
      width: '100%',
    } : {
      flex: 1,
      overflow: 'hidden',
    }),
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    marginLeft: 8,
    paddingRight: 32,
    height: '100%',
    ...(Platform.OS === 'web' && {
      borderWidth: 0,
      outlineWidth: 0,
      outlineStyle: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none',
    }),
  },
  clearButton: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: 0,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  addButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  roomGrid: {
    flexDirection: 'column',
    gap: 12,
  },
  roomCard: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
  },
  roomCardContent: {
    flex: 1,
  },
  roomDetailsRedesign: {
    flex: 1,
    paddingHorizontal: 10,
    justifyContent: 'center',
    minWidth: 0,
  },
  roomNameRedesign: {
    fontSize: 17,
    fontWeight: '500',
    marginBottom: 4,
  },
  roomDetailLabel: {
    fontSize: 13,
    color: '#666',
    marginBottom: 2,
  },
  roomActionsRedesign: {
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    alignSelf: 'stretch',
  },
  actionButtonRedesign: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
    marginLeft: 0,
    ...(Platform.OS === 'web' 
      ? { boxShadow: '0 1px 2px 0 rgba(0,0,0,0.04)' }
      : {
          elevation: 1,
          shadowColor: '#000',
          shadowOpacity: 0.04,
          shadowRadius: 2,
          shadowOffset: { width: 0, height: 1 },
        }
    ),
  },
  paginationContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    width: '100%',
  },
  paginationContainerTop: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  paginationButton: {
    padding: 8,
    borderRadius: 8,
    minWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  emptyText: {
    fontSize: 16,
    marginBottom: 16,
  },
  addFirstButton: {
    borderRadius: 10,
    padding: 16,
  },
  addFirstButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  toast: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    zIndex: 1000,
  },
  toastText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteModalContent: {
    backgroundColor: '#2a2a2a',
    borderRadius: 12,
    padding: 20,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  deleteModalTitle: {
    fontSize: 17,
    fontWeight: '500',
    marginBottom: 12,
    textAlign: 'center',
  },
  deleteModalText: {
    fontSize: 16,
    color: '#fff',
    marginBottom: 20,
    textAlign: 'center',
  },
  deleteModalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
  },
  deleteModalButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
  },
  cancelButton: {
    backgroundColor: '#333',
  },
  deleteButton: {
    backgroundColor: '#ff3b30',
  },
  cancelButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  metaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 4,
    backgroundColor: '#333',
  },
  loadMoreButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  loadMoreButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  pageNumberContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pageNumber: {
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default function RoomList() {
  const navigation = useNavigation<RoomListScreenNavigationProp>();
  const route = useRoute<RoomListScreenRouteProp>();
  const { width } = useWindowDimensions();
  const isSmallScreen = useIsSmallScreen();
  const { theme, themeType } = useTheme();
  const { user } = useAuth();
  const { tier, roomLimit, currency, dateFormat } = useSubscription();
  const {
    rooms,
    isLoading,
    hasMore,
    search,
    setSearch,
    sortBy,
    setSortBy,
    totalCount,
    page,
    PAGE_SIZE,
    refresh,
    changePage
  } = usePaginatedRooms();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<string | null>(null);
  const [showItemsExistModal, setShowItemsExistModal] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, 600);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const scrollViewRef = React.useRef<ScrollView>(null);
  // Desktop-web scroll container
  const wrapperScrollRef = React.useRef<HTMLDivElement>(null);

  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};

  // Helper to get the DOM node used by RN-web ScrollView (mobile web)
  const getWebScroller = () => {
    if (Platform.OS !== 'web') return null;
    // @ts-ignore
    return scrollViewRef.current?.getScrollableNode?.() as HTMLElement | null;
  };

  // Unified scroll-to-top
  const scrollToTop = () => {
    try {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0 });
      }
      if (Platform.OS === 'web' && wrapperScrollRef.current) {
        wrapperScrollRef.current.scrollTo({ top: 0, left: 0 });
      }
      getWebScroller()?.scrollTo({ top: 0, left: 0 });
      scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
    } catch (err) {
      console.error('scrollToTop failed', err);
    }
  };

  // Auto-scroll when page changes (skip first mount)
  const didFirstPageEffect = React.useRef(false);
  useEffect(() => {
    if (!didFirstPageEffect.current) {
      didFirstPageEffect.current = true;
      return;
    }
    // Keep current page items in view when navigating between pages
    scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
  }, [page]);

  console.log('🏠 [RoomList] Component rendered with state:', {
    roomsCount: rooms.length,
    isLoading,
    hasMore,
    page,
    search,
    sortBy,
    totalCount,
    isFocused
  });

  // Sync search input with debounced search
  useEffect(() => {
    console.log('🔄 [RoomList] Search input changed:', searchInput);
    setSearch(searchInput); // Update server query only after debounce to reduce load
  }, [debouncedSearch, setSearch]);

  // Sync search input with search state
  useEffect(() => {
    console.log('🔄 [RoomList] Search state changed:', search);
    setSearchInput(search);
  }, [search]);

  // Focus effect
  useFocusEffect(
    useCallback(() => {
      console.log('👁️ [RoomList] Screen focused');
      setIsFocused(true);
      return () => {
        console.log('👁️ [RoomList] Screen unfocused');
        setIsFocused(false);
      };
    }, [])
  );

  /* ---------------- Persist page across remounts ---------------- */
  useEffect(() => {
    if (route.params?.page && route.params.page !== page) {
      console.log('🔄 [RoomList] Restoring page from params:', route.params.page);
      changePage(route.params.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    navigation.setParams({ page });
  }, [page]);

  // Calculate total pages
  const totalPages = totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 1;

  // Replace deprecated shadow* props with boxShadow
  const shadowStyle = Platform.OS === 'web' 
    ? { boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' } 
    : { 
        elevation: 2,
        shadowColor: '#000', 
        shadowOpacity: 0.25, 
        shadowRadius: 3.84, 
        shadowOffset: { width: 0, height: 2 } 
      };

  const styles = React.useMemo(() => createStyles(isSmallScreen), [isSmallScreen]);

  useEffect(() => {
    if (route.params?.showSuccess && route.params?.message) {
      setToastMessage(route.params.message);
      setShowToast(true);
      Animated.sequence([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.delay(2000),
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start(() => {
        setShowToast(false);
        navigation.setParams({ showSuccess: undefined, message: undefined });
      });
    }
  }, [route.params?.showSuccess, route.params?.message]);

  const handleAddRoom = () => {
    navigation.navigate('AddRoom');
  };

  const handleEditRoom = (roomId: string) => {
    navigation.dispatch(
      CommonActions.navigate({
        name: 'EditRoom',
        params: { 
          roomId,
          returnPage: page // Pass current page to return to
        }
      })
    );
  };

  const handleDeleteRoom = (roomId: string) => {
    setRoomToDelete(roomId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!roomToDelete) return;

    try {
      // First check if the room has any items
      const { data: items, error: itemsError } = await supabase
        .from('items')
        .select('id')
        .eq('room_id', roomToDelete)
        .limit(1);

      if (itemsError) throw itemsError;

      if (items && items.length > 0) {
        setShowDeleteModal(false);
        setShowItemsExistModal(true);
        return;
      }

      const { error } = await supabase
        .from('rooms')
        .delete()
        .eq('id', roomToDelete);

      if (error) throw error;

      // Refresh the room list instantly
      refresh();
      setShowDeleteModal(false);
      setRoomToDelete(null);
    } catch (error) {
      console.error('Error deleting room:', error);
      Alert.alert('Error', 'Failed to delete room');
    }
  };

  const handleSortSelect = (option: SortOption) => {
    setSortBy(option);
    setShowFilterModal(false);
  };

  const handleFilterSelect = (roomId: string | null) => {
    setSearchInput(''); // clear search if filtering by room
    setShowFilterModal(false);
  };

  const renderFilterModal = () => (
    <Modal
      visible={showFilterModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowFilterModal(false)}
    >
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ backgroundColor: theme.card, borderRadius: 20, padding: 32, maxWidth: 400, width: '90%', alignItems: 'center', ...Platform.select({
          ios: {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
          },
          android: {
            elevation: 8,
          },
          web: {
            boxShadow: '0 4px 24px 0 rgba(0,0,0,0.12)',
          },
        }) }}>
          {/* Close button */}
          <TouchableOpacity
            onPress={() => setShowFilterModal(false)}
            style={{ position: 'absolute', right: 16, top: 16, padding: 12, borderRadius: 24, backgroundColor: theme.inputBackground, zIndex: 2 }}
            accessibilityRole="button"
            accessibilityLabel="Close filter and sort modal"
          >
            <Ionicons name="close" size={28} color={theme.text} />
          </TouchableOpacity>
          {/* Title */}
          <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 12, marginTop: 8 }}>Filter & Sort</Text>
          {/* Divider */}
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginVertical: 16 }} />
          {/* Subtitle */}
          <Text style={{ color: theme.text, fontWeight: '500', fontSize: 15, marginBottom: 18, textAlign: 'center' }}>Sort By</Text>
          {/* Sort buttons: Name first, then Most Recent */}
          <View style={{ flexDirection: 'row', gap: 16, justifyContent: 'center', alignItems: 'center', marginBottom: 8 }}>
            {[ 'name', 'recent' ].map(option => (
              <ThemedButton
                key={option}
                title={option === 'name' ? 'Name' : 'Most Recent'}
                onPress={() => handleSortSelect(option as SortOption)}
                variant={sortBy === option ? 'primary' : 'secondary'}
                style={{ minWidth: 110 }}
                accessibilityLabel={option === 'recent' ? 'Sort by most recent' : 'Sort by name'}
              />
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderDeleteModal = () => (
    <Modal
      visible={showDeleteModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowDeleteModal(false)}
    >
      <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
        <View style={[styles.deleteModalContent, { backgroundColor: theme.card }]}>
          <Text style={[styles.deleteModalTitle, { color: theme.text }]}>Delete Room</Text>
          <Text style={[styles.deleteModalText, { color: theme.text }]}>Are you sure you want to delete this room?</Text>
          <View style={[styles.deleteModalButtons, { gap: 12 }]}>
            <ThemedButton
              title="Cancel"
              variant="secondary"
              onPress={() => setShowDeleteModal(false)}
              style={{ flex: 1, minWidth: 140 }}
            />
            <ThemedButton
              title="Delete"
              onPress={confirmDelete}
              style={{ flex: 1, minWidth: 140 }}
            />
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderItemsExistModal = () => (
    <Modal
      visible={showItemsExistModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowItemsExistModal(false)}
    >
      <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
        <View style={[styles.deleteModalContent, { backgroundColor: theme.card }]}>
          <Text style={[styles.deleteModalTitle, { color: theme.text }]}>Cannot Delete Room</Text>
          <Text style={[styles.deleteModalText, { color: theme.text }]}>
            This room contains items that need to be moved or deleted first.
            Please move all items to another room before deleting this room.
          </Text>
          <View style={[styles.deleteModalButtons, { gap: 12 }]}>
            <ThemedButton
              title="OK"
              onPress={() => setShowItemsExistModal(false)}
              style={{ minWidth: 140, alignSelf: 'center' }}
            />
          </View>
        </View>
      </View>
    </Modal>
  );

  // Add a function to determine if a non-default sort is active
  const isSortActive = sortBy !== 'recent';
  const showChips = isSortActive;
  const handleClearAll = () => {
    setSortBy('recent');
  };

  useEffect(() => {
    if (route.params?.refresh) {
      // If a specific page is provided in params, use it
      if (route.params.page) {
        changePage(route.params.page);
      } else {
        refresh();
      }
      
      // Reset refresh flag but preserve page
      navigation.setParams({ 
        refresh: false,
        page: route.params.page || page
      });
    }
  }, [navigation, refresh, route.params, changePage, page]);

  useFocusEffect(
    useCallback(() => {
      if (route.params?.refresh) {
        if (route.params?.page) {
          changePage(route.params.page);
        } else {
          refresh();
        }
      }
    }, [route.params, refresh, changePage, page])
  );

  if (isLoading && page === 1) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
        <ActivityIndicator size="large" color={theme.accent} />
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle={themeType === 'dark' || themeType === 'blueNeon' ? 'light-content' : 'dark-content'} />
      {/* Desktop layout */}
      {!isSmallScreen && Platform.OS === 'web' ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', scrollbarGutter: 'stable' }}>
          {/* Existing desktop header layout - unchanged */}
          <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', paddingHorizontal: 16, marginTop: 16, marginBottom: 16 }}>
            <View style={{ paddingLeft: 0, minWidth: 400, maxWidth: 400 }}>
              <View style={[styles.searchContainer, { backgroundColor: theme.inputBackground, marginBottom: 0 }, glowStyle]}> 
                <Ionicons name="search" size={20} color={theme.text} style={styles.searchIcon} />
                <FormTextInput
                  style={[styles.searchInput, { color: theme.text }]}
                  placeholder="Search rooms..."
                  placeholderTextColor={theme.secondaryText}
                  value={searchInput}
                  onChangeText={setSearchInput}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
                {searchInput.length > 0 && (
                  <TouchableOpacity
                    onPress={() => {
                      setSearchInput('');
                      setSearch('');
                    }}
                    style={styles.clearButton}
                    accessibilityRole="button"
                    accessibilityLabel="Clear search"
                  >
                    <Ionicons name="close-circle" size={20} color={theme.text} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
            <View style={{ flex: 1 }} />
            <View style={{ flexDirection: 'row', gap: 8, marginRight: 16 }}>
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  { backgroundColor: sortBy !== 'name' ? theme.accent : theme.inputBackground }
                ]}
                onPress={() => setShowFilterModal(true)}
                accessibilityRole="button"
                accessibilityLabel="Filter and sort rooms"
              >
                <Ionicons name="options-outline" size={24} color={theme.text} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.addButton, { backgroundColor: theme.accent }]}
                onPress={handleAddRoom}
                accessibilityRole="button"
                accessibilityLabel="Add new room"
              >
                <Ionicons name="add" size={24} color={theme.buttonText} />
              </TouchableOpacity>
            </View>
          </View>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%' }} />
          <View style={{ height: 16 }} />
          <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', paddingHorizontal: 16, marginBottom: 16 }}>
            <Text style={[styles.paginationText, { color: theme.text, textAlign: 'left', paddingLeft: 0 }]}> 
              {(() => {
                if ((totalCount || 0) === 0) return 'No rooms found';
                const startItem = ((page - 1) * PAGE_SIZE) + 1;
                const endItem = Math.min(page * PAGE_SIZE, totalCount || 0);
                let text = `Showing ${startItem}-${endItem} of ${totalCount || 0}`;
                if (search.trim().length > 0) text += ' (search results)'; // Clarify when a filter is active
                return text;
              })()}
            </Text>
            <View style={{ flex: 1 }} />
            {/* Existing desktop pagination - unchanged */}
            {totalPages > 1 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 16 }}>
                {/* Keep existing pagination buttons unchanged */}
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === 1 && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} style={{ marginLeft: -12 }} />
                  </View>
                </TouchableOpacity>
               
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === 1 && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(page - 1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                </TouchableOpacity>
              
                <Text style={[styles.paginationText, { color: theme.text, textAlign: 'right', marginHorizontal: 8 }]}>Page {page} of {totalPages}</Text>
                
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === totalPages && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(page + 1);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === totalPages && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(totalPages);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} style={{ marginRight: -12 }} />
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                  </View>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Cards – independent scroll area */}
          <div style={{ flex: 1, overflowY: 'auto' }} ref={wrapperScrollRef}>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingBottom: 32 }}
              ref={scrollViewRef}
              showsVerticalScrollIndicator
            >
              <View style={{ paddingHorizontal: 16 }}>
                <View style={[styles.roomGrid, themeType === 'blueNeon' && { paddingTop: 8 }]}>
                  {rooms.map((room) => (
                    <View
                      key={room.id}
                      style={[styles.roomCard, { backgroundColor: theme.card, borderColor: theme.border, flexDirection: 'row', alignItems: 'center' }, glowStyle]}
                    >
                      <View style={styles.roomCardContent}>
                        <View style={styles.roomDetailsRedesign}>
                          <Text style={[styles.roomNameRedesign, { color: theme.text }]} numberOfLines={1}>{room.name}</Text>
                          {room.description && (
                            <Text style={[styles.roomDetailLabel, { color: theme.secondaryText }]} numberOfLines={2}>{room.description}</Text>
                          )}
                          <Text style={[styles.roomDetailLabel, { color: theme.secondaryText }]}>
                            {room.items?.[0]?.count ?? 0} {(room.items?.[0]?.count ?? 0) === 1 ? 'item' : 'items'}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.roomActionsRedesign}>
                        <TouchableOpacity
                          style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]}
                          onPress={() => handleEditRoom(room.id)}
                          activeOpacity={0.8}
                          accessibilityRole="button"
                          accessibilityLabel={`Edit room ${room.name}`}
                        >
                          <Ionicons name="pencil" size={20} color={theme.accent} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]}
                          onPress={() => handleDeleteRoom(room.id)}
                          activeOpacity={0.8}
                          accessibilityRole="button"
                          accessibilityLabel={`Delete room ${room.name}`}
                        >
                          <Ionicons name="trash" size={20} color={theme.accent} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </ScrollView>
          </div>
        </div>
      ) : (
        /* Mobile layout */
        <View style={{ flex: 1 }}>
          {/* Mobile header */}
          <View style={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8 }}>
            {/* Search and buttons row */}
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
              <View style={{ flex: 1 }}>
                <View style={[styles.searchContainer, { backgroundColor: theme.inputBackground, marginBottom: 0 }, glowStyle]}> 
                  <Ionicons name="search" size={20} color={theme.text} style={styles.searchIcon} />
                  <FormTextInput
                    style={[styles.searchInput, { color: theme.text }]}
                    placeholder="Search rooms..."
                    placeholderTextColor={theme.secondaryText}
                    value={searchInput}
                    onChangeText={setSearchInput}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                  {searchInput.length > 0 && (
                    <TouchableOpacity
                      onPress={() => {
                        setSearchInput('');
                        setSearch('');
                      }}
                      style={styles.clearButton}
                      accessibilityRole="button"
                      accessibilityLabel="Clear search"
                    >
                      <Ionicons name="close-circle" size={20} color={theme.text} />
                    </TouchableOpacity>
                  )}
                </View>
              </View>
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  { backgroundColor: sortBy !== 'name' ? theme.accent : theme.inputBackground }
                ]}
                onPress={() => setShowFilterModal(true)}
                accessibilityRole="button"
                accessibilityLabel="Filter and sort rooms"
              >
                <Ionicons name="options-outline" size={24} color={theme.text} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.addButton, { backgroundColor: theme.accent }]}
                onPress={handleAddRoom}
                accessibilityRole="button"
                accessibilityLabel="Add new room"
              >
                <Ionicons name="add" size={24} color={theme.buttonText} />
              </TouchableOpacity>
            </View>
          </View>
          <View style={{ height: 1, backgroundColor: theme.border }} />
          {/* Mobile pagination */}
          <View style={{ 
            paddingHorizontal: 16, 
            paddingVertical: 12, 
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '100%'
          } as const}>
            <Text style={[styles.paginationText, { color: theme.text }]}>
              {(() => {
                if ((totalCount || 0) === 0) return 'No rooms found';
                const startItem = ((page - 1) * PAGE_SIZE) + 1;
                const endItem = Math.min(page * PAGE_SIZE, totalCount || 0);
                let text = `Displaying ${startItem}-${endItem} of ${totalCount || 0} rooms`;
                if (search.trim().length > 0) text += ' (search results)';
                return text;
              })()}
            </Text>
            {totalPages > 1 && (
              <View style={{ 
                flexDirection: 'row', 
                alignItems: 'center', 
                gap: 8, 
                marginTop: 8,
                justifyContent: 'center',
                width: '100%'
              } as const}>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === 1 && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} style={{ marginLeft: -12 }} />
                  </View>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === 1 && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(page - 1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                </TouchableOpacity>
                <Text style={[styles.paginationText, { color: theme.text, marginHorizontal: 8 }]}>
                  Page {page} of {totalPages}
                </Text>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === totalPages && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(page + 1);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === totalPages && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(totalPages);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} style={{ marginRight: -12 }} />
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                  </View>
                </TouchableOpacity>
              </View>
            )}
          </View>
          <View style={{ height: 1, backgroundColor: theme.border, marginBottom: 16 }} />
          {/* Mobile cards list */}
          <ScrollView
            style={{ flex: 1, height: '100%' }}
            contentContainerStyle={{ paddingBottom: 32, flexGrow: 1 }}
            ref={scrollViewRef}
            showsVerticalScrollIndicator
          >
            <View style={{ paddingHorizontal: 16 }}>
              <View style={[styles.roomGrid, themeType === 'blueNeon' && { paddingTop: 8 }]}>
                {rooms.map((room) => (
                  <View
                    key={room.id}
                    style={[
                      styles.roomCard,
                      {
                        backgroundColor: theme.card,
                        borderColor: theme.border,
                        flexDirection: 'row',
                        alignItems: 'center',
                      },
                      glowStyle,
                    ]}
                  >
                    <View style={styles.roomCardContent}>
                      <View style={styles.roomDetailsRedesign}>
                        <Text style={[styles.roomNameRedesign, { color: theme.text }]} numberOfLines={1}>
                          {room.name}
                        </Text>
                        {room.description && (
                          <Text style={styles.roomDetailLabel} numberOfLines={2}>
                            {room.description}
                          </Text>
                        )}
                        <Text style={styles.roomDetailLabel}>
                          {room.items?.[0]?.count ?? 0}{' '}
                          {(room.items?.[0]?.count ?? 0) === 1 ? 'item' : 'items'}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.roomActionsRedesign}>
                      <TouchableOpacity
                        style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]}
                        onPress={() => handleEditRoom(room.id)}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityLabel={`Edit room ${room.name}`}
                      >
                        <Ionicons name="pencil" size={20} color={theme.accent} />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]}
                        onPress={() => handleDeleteRoom(room.id)}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityLabel={`Delete room ${room.name}`}
                      >
                        <Ionicons name="trash" size={20} color={theme.accent} />
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>
        </View>
      )}
      {showFilterModal && renderFilterModal()}
      {showDeleteModal && renderDeleteModal()}
      {showItemsExistModal && renderItemsExistModal()}
      {showToast && (
        <Animated.View
          style={[
            styles.toast,
            {
              backgroundColor: theme.accent,
              opacity: fadeAnim,
            },
          ]}
        >
          <Text style={[styles.toastText, { color: theme.buttonText }]}>{toastMessage}</Text>
        </Animated.View>
      )}
    </SafeAreaView>
  );
} 